import { z } from 'zod';

/** Grams in one troy ounce; spot gold is quoted per ounce. */
export const TROY_OUNCE_GRAMS = 31.1035;

/** Gram gold in TRY from the USD ounce price and the USD/TRY rate. */
export function gramGoldTry(ounceUsd: number, usdTry: number): number {
  return (ounceUsd * usdTry) / TROY_OUNCE_GRAMS;
}

/** Percent change of a product of two prices whose individual changes are known. */
export function combineChangePct(a: number, b: number): number {
  return ((1 + a / 100) * (1 + b / 100) - 1) * 100;
}

// ---------- Server responses ----------

export const geocodeQuery = z.object({ q: z.string().trim().min(2).max(80) });
export const weatherQuery = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lon: z.coerce.number().min(-180).max(180),
});

export const locationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
});
export type WidgetLocation = z.infer<typeof locationSchema>;

export interface GeocodeResult extends WidgetLocation {
  /** Region and country, to tell apart cities that share a name. */
  detail: string;
}
export interface GeocodeResponse {
  results: GeocodeResult[];
  stale: boolean;
}

export interface WeatherHour {
  /** Local time at the location, `YYYY-MM-DDTHH:mm`. */
  time: string;
  temperature: number;
  weatherCode: number;
  isDay: boolean;
  precipitationProbability: number | null;
}
export interface WeatherResponse {
  current: {
    time: string;
    temperature: number;
    weatherCode: number;
    isDay: boolean;
    windKmh: number;
    precipitationMm: number;
  };
  /** Starts at the current hour. */
  hourly: WeatherHour[];
  stale: boolean;
}

export const MARKET_IDS = ['usd', 'eur', 'gold', 'btc', 'ceyrek', 'yarim', 'tam', 'cumhuriyet', 'ayar22'] as const;
export type MarketId = (typeof MARKET_IDS)[number];
/** Shown until the user picks their own list; the other gold products are opt-in. */
export const DEFAULT_MARKETS: MarketId[] = ['usd', 'eur', 'gold', 'btc'];

export interface MarketQuote {
  id: MarketId;
  value: number;
  currency: 'TRY' | 'USD';
  /** Percent change against the previous close (or 24 hours ago); null when the source has none. */
  changePct: number | null;
  /** Where the number comes from, e.g. `TCMB`. */
  source: string;
  /** The source's own date or timestamp label (`29.09.2026`), when it publishes one. */
  asOf?: string;
  /** This quote is the last known value because its source failed on the latest refresh. */
  stale?: true;
}
export interface MarketsResponse {
  items: MarketQuote[];
  stale: boolean;
}

// ---------- Info bar preferences ----------

export const WIDGET_IDS = ['weather', 'markets', 'netWorth', 'bell'] as const;
export type WidgetId = (typeof WIDGET_IDS)[number];

export const DEFAULT_LOCATION: WidgetLocation = { name: 'Ankara', lat: 39.93, lon: 32.86 };

export const MAX_LOCATIONS = 5;

const infoBarShape = z.object({
  visible: z.boolean().default(true),
  /** Net worth shows as dots until the user reveals it; remembered per user. */
  hideNetWorth: z.boolean().default(true),
  /** `above`: docked over the desk and reserving its height. `behind`: on the desk layer, windows cover it. */
  position: z.enum(['above', 'behind']).default('above'),
  widgets: z
    .object({
      weather: z.boolean().default(true),
      markets: z.boolean().default(true),
      /** Unset means automatic: shown once the user has a Kasa account. */
      netWorth: z.boolean().optional(),
      bell: z.boolean().default(true),
    })
    .default({ weather: true, markets: true, bell: true }),
  /** Saved cities; the first is the primary one the bar shows in full. */
  locations: z.array(locationSchema).min(1).max(MAX_LOCATIONS).default([DEFAULT_LOCATION]),
  /** The clock shows HH:mm unless this is on. */
  showSeconds: z.boolean().default(false),
  /** The bar's date next to the clock. */
  showDate: z.boolean().default(true),
  markets: z
    .array(z.enum(MARKET_IDS))
    .max(MARKET_IDS.length)
    .default([...DEFAULT_MARKETS]),
});

/** Stored before there were several cities: a single `location` becomes the only entry of `locations`. */
function migrateLegacyLocation(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const { location, ...rest } = raw as Record<string, unknown>;
  if (location === undefined || 'locations' in rest) return rest;
  return { ...rest, locations: [location] };
}

export const infoBarPrefs = z.preprocess(migrateLegacyLocation, infoBarShape);
export type InfoBarPrefs = z.infer<typeof infoBarShape>;

/** Wallpaper id meaning "no picture": the plain drafting grid. */
export const NO_WALLPAPER = 'none';
/** Shown until the user picks another; also what an id that no longer exists resolves to. */
export const DEFAULT_WALLPAPER = 'halka';

/** Ids are validated against the bundled wallpapers by the client, so the catalogue can grow without a server change. */
export const wallpaperPref = z.object({ id: z.string().trim().min(1).max(40) });
export type WallpaperPref = z.infer<typeof wallpaperPref>;

/** Stored preference keys and the shape each value must have. */
export const PREF_SCHEMAS = { infoBar: infoBarPrefs, wallpaper: wallpaperPref } as const;
export type PrefKey = keyof typeof PREF_SCHEMAS;
export const PREF_MAX_BYTES = 4096;
